import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

/**
 * Autocomplete que sólo permite valores existentes en la tabla `airports`
 * o `destinations`. Si no hay coincidencia, ofrece un botón para "Solicitar
 * alta" que crea un registro pendiente que admin debe aprobar.
 *
 * Props:
 *  - kind: "airport" | "destination"
 *  - value: { codigo, nombre } | null
 *  - onSelect(item)   → recibe { codigo, nombre, tipo, country? }
 *  - onClear()
 *  - testid, placeholder
 */
export default function LugarAutocomplete({ kind, value, onSelect, onClear, testid, placeholder }) {
  const [query, setQuery] = useState(null); // null → mostrar value; string → busqueda
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [asking, setAsking] = useState(false);
  const [askForm, setAskForm] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    const load = async () => {
      const url = kind === "airport" ? `${API}/airports` : `${API}/destinations`;
      const r = await axios.get(url);
      setItems(r.data || []);
    };
    load();
  }, [kind]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(null); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const shown = useMemo(() => {
    if (query !== null) return query;
    if (value?.nombre) return value.nombre;
    return "";
  }, [query, value]);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return items.slice(0, 20);
    if (kind === "airport") {
      return items.filter((a) =>
        a.iata_code?.toLowerCase().includes(q) ||
        a.name?.toLowerCase().includes(q) ||
        a.city?.toLowerCase().includes(q)
      ).slice(0, 25);
    }
    return items.filter((d) =>
      d.city?.toLowerCase().includes(q) ||
      d.state?.toLowerCase().includes(q) ||
      d.country?.toLowerCase().includes(q)
    ).slice(0, 25);
  }, [items, query, kind]);

  const pick = (item) => {
    setQuery(null);
    setOpen(false);
    if (kind === "airport") {
      onSelect({
        codigo: item.iata_code,
        nombre: `${item.iata_code} · ${item.city || item.name}`,
        tipo: "airport",
        country: item.country || "",
        is_pending: !!item.is_pending,
      });
    } else {
      onSelect({
        codigo: item.id,
        nombre: `${item.city}${item.state ? " · " + item.state : ""}${item.country && item.country !== "México" ? " · " + item.country : ""}`,
        tipo: "destination",
        country: item.country || "",
        is_pending: !!item.is_pending,
      });
    }
  };

  const abrirSolicitud = () => {
    const q = (query || "").trim();
    if (kind === "airport") {
      setAskForm({ iata_code: q.toUpperCase().slice(0, 4), name: "", city: "", country: "" });
    } else {
      setAskForm({ city: q, state: "", country: "México" });
    }
    setAsking(true);
  };

  const enviarSolicitud = async () => {
    try {
      const endpoint = kind === "airport" ? "solicitar-aeropuerto" : "solicitar-destino";
      const r = await axios.post(`${API}/cabina/${endpoint}`, askForm);
      toast.success(kind === "airport" ? `Aeropuerto ${r.data.iata_code} solicitado. Espera aprobación.` : `Destino "${askForm.city}" solicitado. Espera aprobación.`);
      // Recargar catálogo
      const url = kind === "airport" ? `${API}/airports/all` : `${API}/destinations`;
      const rr = await axios.get(url).catch(() => ({ data: items }));
      setItems(rr.data || items);
      // Auto-seleccionar el recién creado
      if (kind === "airport") {
        pick({ iata_code: r.data.iata_code, city: askForm.city, name: askForm.name, country: askForm.country, is_pending: true });
      } else {
        pick({ id: r.data.id, city: askForm.city, state: askForm.state, country: askForm.country, is_pending: true });
      }
      setAsking(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar solicitud");
    }
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        value={shown}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); if (e.target.value === "" && onClear) onClear(); }}
        onFocus={() => { setOpen(true); setQuery(query ?? ""); }}
        placeholder={placeholder || (kind === "airport" ? "Buscar IATA o ciudad..." : "Buscar destino...")}
        autoComplete="off"
        data-testid={testid}
      />
      {value?.is_pending && <div className="hint" style={{ color: "var(--cv-night)", marginTop: 3 }}>⚠ Pendiente de aprobación por admin</div>}
      {open && !asking && (
        <div style={{ position: "absolute", zIndex: 50, top: "100%", left: 0, right: 0, marginTop: 3, background: "#fff", border: "1px solid var(--cv-line)", borderRadius: 8, boxShadow: "0 8px 24px rgba(15,23,42,.12)", maxHeight: 260, overflow: "auto" }}>
          {filtered.length > 0 ? filtered.map((it) => (
            <button key={kind === "airport" ? it.iata_code : it.id} type="button"
              onMouseDown={(ev) => { ev.preventDefault(); pick(it); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "#fff", border: 0, borderBottom: "1px solid #f1f5f9", cursor: "pointer", fontSize: 13 }}>
              {kind === "airport" ? (
                <>
                  <b style={{ color: "var(--cv-blue)" }}>{it.iata_code}</b> · {it.name}
                  <div style={{ fontSize: 11, color: "var(--cv-muted)" }}>{it.city}{it.country ? `, ${it.country}` : ""}{it.is_pending && " · pendiente"}</div>
                </>
              ) : (
                <>
                  <b>{it.city}</b>{it.state ? ` · ${it.state}` : ""}
                  <div style={{ fontSize: 11, color: "var(--cv-muted)" }}>{it.country}{it.is_pending && " · pendiente"}</div>
                </>
              )}
            </button>
          )) : (
            <div style={{ padding: 14, textAlign: "center" }}>
              <p style={{ margin: 0, fontSize: 12, color: "var(--cv-muted)" }}>Sin coincidencias</p>
              {(query || "").trim().length >= 2 && (
                <button type="button" className="btn ghost sm" style={{ marginTop: 8 }} onMouseDown={(ev) => { ev.preventDefault(); abrirSolicitud(); }} data-testid={`${testid}-solicitar`}>
                  + Solicitar alta de &quot;{query}&quot;
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {open && asking && (
        <div style={{ position: "absolute", zIndex: 50, top: "100%", left: 0, right: 0, marginTop: 3, background: "#fff", border: "1px solid var(--cv-line)", borderRadius: 8, boxShadow: "0 8px 24px rgba(15,23,42,.12)", padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Solicitar alta {kind === "airport" ? "de aeropuerto" : "de destino"}</div>
          {kind === "airport" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <label className="f"><span>IATA (3)</span><input value={askForm.iata_code || ""} maxLength={4} onChange={(e) => setAskForm({ ...askForm, iata_code: e.target.value.toUpperCase() })} /></label>
              <label className="f"><span>Nombre</span><input value={askForm.name || ""} onChange={(e) => setAskForm({ ...askForm, name: e.target.value })} /></label>
              <label className="f"><span>Ciudad</span><input value={askForm.city || ""} onChange={(e) => setAskForm({ ...askForm, city: e.target.value })} /></label>
              <label className="f"><span>País</span><input value={askForm.country || ""} onChange={(e) => setAskForm({ ...askForm, country: e.target.value })} placeholder="MX" /></label>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <label className="f" style={{ gridColumn: "1 / -1" }}><span>Ciudad / destino</span><input value={askForm.city || ""} onChange={(e) => setAskForm({ ...askForm, city: e.target.value })} /></label>
              <label className="f"><span>Estado</span><input value={askForm.state || ""} onChange={(e) => setAskForm({ ...askForm, state: e.target.value })} /></label>
              <label className="f"><span>País</span><input value={askForm.country || ""} onChange={(e) => setAskForm({ ...askForm, country: e.target.value })} /></label>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 8 }}>
            <button type="button" className="btn ghost sm" onMouseDown={(ev) => { ev.preventDefault(); setAsking(false); }}>Cancelar</button>
            <button type="button" className="btn dark sm" onMouseDown={(ev) => { ev.preventDefault(); enviarSolicitud(); }} data-testid={`${testid}-enviar`}>Enviar solicitud</button>
          </div>
        </div>
      )}
    </div>
  );
}
